import { Brain, CheckCircle2, Info, XCircle, Bell, Heart } from "lucide-react";
import { Iconly } from "@/components/ui/iconly-wrapper";
import { cn } from "@/lib/utils";
import type { NotificationType } from "@/lib/notification-store";
import type { ProgressTheme } from "@/lib/progress-store";
import type { HeaderIslandMode, HeaderNotificationState } from "./types";

const getNotificationVisuals = (type: NotificationType) => {
  switch (type) {
    case "success":
      return {
        icon: "text-emerald-600",
        title: "text-emerald-950",
        description: "text-emerald-700/70",
      };
    case "error":
      return {
        icon: "text-rose-600",
        title: "text-rose-950",
        description: "text-rose-700/70",
      };
    case "warning":
      return {
        icon: "text-amber-700",
        title: "text-amber-950",
        description: "text-amber-700/72",
      };
    case "info":
      return {
        icon: "text-sky-600",
        title: "text-sky-950",
        description: "text-sky-700/70",
      };
    case "library":
      return {
        icon: "text-rose-500",
        title: "text-rose-950",
        description: "text-rose-700/70",
      };
    case "sparkles":
      return {
        icon: "text-violet-600",
        title: "text-violet-950",
        description: "text-violet-700/72",
      };
    default:
      return {
        icon: "text-slate-600",
        title: "text-slate-950",
        description: "text-slate-500",
      };
  }
};

const getNotificationIcon = (type: NotificationType) => {
  const visuals = getNotificationVisuals(type);
  const base = "flex h-10 w-10 shrink-0 items-center justify-center";
  switch (type) {
    case "success":
      return <div className={cn(base, visuals.icon)}><CheckCircle2 className="h-5 w-5" /></div>;
    case "error":
      return <div className={cn(base, visuals.icon)}><XCircle className="h-5 w-5" /></div>;
    case "warning":
      return <div className={cn(base, visuals.icon)}><Info className="h-5 w-5" /></div>;
    case "info":
      return <div className={cn(base, visuals.icon)}><Bell className="h-5 w-5" /></div>;
    case "library":
      return <div className={cn(base, visuals.icon)}><Heart className="h-5 w-5 fill-current" /></div>;
    case "sparkles":
      return <div className={cn(base, visuals.icon)}><Brain className="h-5 w-5" /></div>;
    default:
      return <div className={cn(base, visuals.icon)}><Bell className="h-5 w-5" /></div>;
  }
};

const getProgressThemeStyles = (theme: ProgressTheme) => {
  const configs = {
    blue: {
      bg: "bg-blue-50/95",
      border: "border-blue-200/80",
      waveFront: "rgba(59, 130, 246, 0.82)",
      waveBack: "rgba(147, 197, 253, 0.5)",
      text: "text-blue-700",
      shadow: "shadow-[0_12px_36px_-10px_rgba(59,130,246,0.38)]",
    },
    green: {
      bg: "bg-green-50/95",
      border: "border-green-200/80",
      waveFront: "rgba(34, 197, 94, 0.82)",
      waveBack: "rgba(134, 239, 172, 0.5)",
      text: "text-green-700",
      shadow: "shadow-[0_12px_36px_-10px_rgba(34,197,94,0.35)]",
    },
    emerald: {
      bg: "bg-emerald-50/95",
      border: "border-emerald-200/80",
      waveFront: "rgba(16, 185, 129, 0.82)",
      waveBack: "rgba(110, 231, 183, 0.52)",
      text: "text-emerald-700",
      shadow: "shadow-[0_12px_36px_-10px_rgba(16,185,129,0.35)]",
    },
  } as const;

  return configs[theme];
};

export function HeaderNotificationIsland({
  islandMode,
  isIslandActive,
  notification,
  onAction,
  progress,
  progressLabel,
  progressTheme,
}: {
  islandMode: HeaderIslandMode;
  isIslandActive: boolean;
  notification: HeaderNotificationState;
  onAction: () => void;
  progress: number;
  progressLabel: string;
  progressTheme: ProgressTheme;
}) {
  const progressStyles = getProgressThemeStyles(progressTheme);
  const progressPercent = Math.round(progress);
  const progressWaveY = 36 - (Math.min(Math.max(progress, 0), 100) / 100) * 26;
  const icon = getNotificationIcon(notification.type);
  const visuals = getNotificationVisuals(notification.type);
  const isActionMode = islandMode === "action";

  const isInteractive = Boolean(notification.link || notification.actionLabel);

  return (
    <div
      onClick={isInteractive ? onAction : undefined}
      className={cn(
        "relative flex h-full w-full items-center overflow-hidden transition-all duration-150 ease-out",
        isIslandActive ? "opacity-100" : "opacity-0",
        isInteractive && "cursor-pointer active:scale-[0.98]",
      )}
    >
      {islandMode === "progress" ? (
        <>
          <div className="absolute inset-0">
            <svg
              viewBox="0 0 100 40"
              preserveAspectRatio="none"
              className="absolute inset-x-0 bottom-0 h-[260%] w-[260%] -translate-x-[18%]"
              aria-hidden="true"
            >
              <path
                className="animate-wave-slow"
                fill={progressStyles.waveBack}
                d={`M 0 ${progressWaveY + 2} Q 16 ${progressWaveY + 5} 32 ${progressWaveY + 2} T 64 ${progressWaveY + 2} T 100 ${progressWaveY + 2} V 40 H 0 Z`}
              />
              <path
                className="animate-wave"
                fill={progressStyles.waveFront}
                d={`M 0 ${progressWaveY} Q 16 ${progressWaveY - 4} 32 ${progressWaveY} T 64 ${progressWaveY} T 100 ${progressWaveY} V 40 H 0 Z`}
              />
            </svg>
          </div>
          <div className="relative z-10 flex h-full w-full items-center justify-between gap-3 px-5">
            <span className={cn("min-w-0 truncate text-[11px] font-bold uppercase tracking-[0.24em] opacity-90", progressStyles.text)}>
              {progressLabel}
            </span>
            <span className={cn("shrink-0 text-sm font-black tracking-wide", progressStyles.text)}>{progressPercent}%</span>
          </div>
        </>
      ) : (
        <div className="flex w-full items-center justify-center gap-3 px-4">
          <div className="flex shrink-0 items-center justify-center">
            {icon}
          </div>
          <div className="min-w-0 text-center">
            <div className={cn("truncate text-[14px] font-bold leading-tight tracking-tight", visuals.title)}>
              {notification.message}
            </div>
            {notification.description ? (
              <div className={cn("mt-0.5 truncate text-[12px] font-semibold leading-tight", visuals.description)}>
                {notification.description}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
